import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { twMerge } from 'tailwind-merge';

import { Factory, Ship, TriangleAlert } from '@repo/media/lucide';

import { Badge } from '@repo/components/badge';

import { formatLocalDate } from '@repo/utils/format-date';
import { useNumberFormatter } from '@repo/utils/use-number-formatter';

import { gridStroke, LegendItem, LegendSwatch, tickStyle, tooltipItemStyle, tooltipLabelStyle, tooltipStyle } from '../charts/chart-look.tsx';
import { useTicking } from '../counter/use-ticking.ts';
import type { MapPosition, ThemeMode } from '../vessel-map/vessel-map.tsx';
import { VesselMap } from '../vessel-map/vessel-map.tsx';
import { Dial } from './dial.tsx';
import type { OverviewDashboard as Data, OverviewCounterPoint, OverviewFlow } from './types.ts';
import { WellSchematic } from './well-schematic.tsx';

// The Northern Lights overview in the layout of the Grafana dashboard it replaces, on a white page (DESIGN.md). It is for
// wall screens, so tonnes are plain text, not <Mt> click-to-copy (DESIGN.md exempts it):
// shipping left, the terminal (CO₂ flows over their graphs, then the tank farm) in the middle, the wells right.
// Each domain has its own title; rules split the domains. Series colours in the design order: teal, sand, coral.
const TEAL = 'var(--color-chart-teal)';
const SAND = 'var(--color-chart-sand)';
const CORAL = 'var(--color-chart-coral)';
const MINUTE_MS = 60_000;
const DASH = '—';
// Chart ticks in rem, so they scale with the page on a wall screen.
const TICK = { ...tickStyle, fontSize: '0.6875rem' };
// A column after the first: a rule on its left, below lg the domains stack.
const RULED = 'lg:border-l lg:border-secondary-200 lg:pl-8';

type Props = {
  data: Data;
  theme: ThemeMode;
  className?: string | undefined;
};

export function OverviewDashboard({ data, theme, className }: Props) {
  const one = useFixed(1);
  const none = useFixed(0);
  const levels = data.tankFarm.tanks.flatMap(t => (t.levelPct == null ? [] : [t.levelPct]));
  const avgLevel = levels.length === 0 ? null : levels.reduce((a, b) => a + b, 0) / levels.length;
  return (
    <div className={twMerge('grid gap-x-8 lg:grid-cols-[minmax(0,8fr)_minmax(0,9fr)_minmax(0,6fr)]', className)}>
      <div className="flex min-w-0 flex-col pb-8">
        <DomainTitle>Shipping</DomainTitle>
        <ShorePower power={data.shorePower} />
        <Section title="Ship location" note="Last 3 days">
          <ShipMap data={data} theme={theme} />
        </Section>
        <Section title="Vessels">
          <Vessels vessels={data.vessels} />
        </Section>
      </div>

      <div className={twMerge('@container flex min-w-0 flex-col pb-8', RULED)}>
        <DomainTitle>Terminal</DomainTitle>
        {/* Each flow over its graph; side by side the two share rows, so the graphs line up. */}
        <div className="grid gap-x-6 @sm:grid-flow-col @sm:grid-cols-2 @sm:grid-rows-[auto_auto]">
          <Section title={<FlowTitle name="Unloading (Q3)" status={q3Status(data)} />}>
            <FlowDial
              name="Unloading (Q3)"
              flow={data.q3Liquid}
              peak={maxOf([...data.series.q3Liquid, ...data.series.q3Vapour])}
              secondary={data.q3Vapour.rateMtPerHour}
            />
            <TotalsPart
              label={(
                <>
                  Liquid
                  {/* Sand, the colour of the vapour return's pointer on the dial. */}
                  <span style={{ color: SAND }}> ( - vapour )</span>
                </>
              )}
              flow={data.q3Liquid}
              minus={data.q3Vapour}
              asOf={data.asOf}
            />
          </Section>
          <Section title="Q3 flow rate" note="t/h · last 12 h">
            <Q3Chart data={data} />
          </Section>
          <Section title={<FlowTitle name="Injection (Q4)" status={q4Status(data)} />}>
            <FlowDial name="Injection (Q4)" flow={data.q4Liquid} peak={maxOf(data.series.q4)} />
            <TotalsPart label="Liquid" flow={data.q4Liquid} asOf={data.asOf} />
          </Section>
          <Section title="Injection flow rate" note="t/h · last 12 h">
            <InjectionChart data={data} />
          </Section>
        </div>
        <Section title="Tank farm" className="mt-6 border-t border-secondary-200">
          <Kpis
            items={[
              ['Total liquid', one(data.tankFarm.volumeM3), 'm³', avgLevel == null ? null : <span title="Average tank level">{`${none(avgLevel)}% avg`}</span>],
              ['Pressure', one(data.tankFarm.pressureBar), 'bar'],
              ['Temperature', one(data.tankFarm.temperatureC), '°C'],
            ]}
            className="pt-0"
          />
        </Section>
        <Section title="Individual tank level">
          <TankBars tanks={data.tankFarm.tanks} />
        </Section>
      </div>

      <div className={twMerge('flex min-w-0 flex-col pb-8', RULED)}>
        <DomainTitle>Aurora wells</DomainTitle>
        {data.wells.map(well => (
          <Section key={well.name} title={`${well.name} well`} aside={wellStatus(well)}>
            <Well well={well} />
          </Section>
        ))}
        <Section title="Final stored" note="all time" className="mt-6 border-t border-secondary-200 lg:flex-1">
          <Stored points={data.stored} asOf={data.asOf} />
        </Section>
      </div>
    </div>
  );
}

// A flow above this reads as running; below it the meters show noise.
const RUNNING_MT_PER_HOUR = 1;

function running(rate: number | null) {
  return rate == null ? null : rate > RUNNING_MT_PER_HOUR;
}

// The state under the flow's name: green while it runs; idle is grey for Q3 and amber for Q4.
// A running flow pulses faintly, so the screen reads as live.
type FlowStatus = { text: string; tone: string; live?: boolean };

function q3Status(data: Data): FlowStatus | null {
  const on = running(data.q3Liquid.rateMtPerHour);
  return on == null ? null : on ? { text: 'Currently unloading', tone: 'text-green-700', live: true } : { text: 'Idle', tone: 'text-secondary-600' };
}

function q4Status(data: Data): FlowStatus | null {
  if (data.q4ReverseFlow === true)
    return { text: 'Reverse flow', tone: 'text-amber-700' };
  const on = running(data.q4Liquid.rateMtPerHour);
  return on == null ? null : on ? { text: 'Currently injecting', tone: 'text-green-700', live: true } : { text: 'Idle', tone: 'text-amber-700' };
}

function FlowTitle({ name, status }: { name: string; status: FlowStatus | null }) {
  return (
    <>
      {name}
      {/* Its own line, so a narrow column does not wrap the heading. */}
      {status != null && <span className={twMerge('mt-0.5 block text-sm font-medium normal-case tracking-normal', status.tone, status.live === true && 'motion-safe:animate-live')}>{status.text}</span>}
    </>
  );
}
function wellStatus(well: Data['wells'][number]) {
  const on = running(well.injectionRateMtPerHour);
  return on == null ? null : <Badge color={on ? 'info' : 'neutral'}>{on ? 'Injecting' : 'Not injecting'}</Badge>;
}

// A rate on a dial from 0 to the max in the Øygarden terminal setup, or to the 12 h peak while the setup has none;
// Q4's dial marks the range below the min injection rate.
function FlowDial({ name, flow, peak, secondary }: {
  name: string;
  flow: OverviewFlow;
  peak: number;
  // Q3's vapour return: a thin pointer, and a −X suffix after the number.
  secondary?: number | null | undefined;
}) {
  const one = useFixed(1);
  const none = useFixed(0);
  const max = flow.maxMtPerHour ?? roundTicks(peak, 4).at(-1) ?? 1;
  const text = one(flow.rateMtPerHour);
  return (
    <div className="flex min-w-0 flex-col items-center">
      <Dial
        value={flow.rateMtPerHour}
        secondary={secondary}
        zoneMax={flow.minMtPerHour}
        max={max}
        color={TEAL}
        secondaryColor={SAND}
        label={`${name}: ${text ?? 'no reading'} t/h of ${none(max)} t/h`}
      />
      <p className="-mt-5 whitespace-nowrap text-xl font-semibold tabular-nums tracking-tight text-secondary-900">
        {text ?? <span className="text-secondary-600">{DASH}</span>}
        {text != null && <span className="ml-1 text-sm font-medium tracking-normal text-secondary-500">t/h</span>}
        {secondary != null && (
          <span className="ml-1.5 text-xs font-normal tracking-normal" style={{ color: SAND }} title="Vapour return">
            {`−${one(secondary)}`}
          </span>
        )}
      </p>
    </div>
  );
}

// Shore power: the last 24 h on the left; on the right the live kW on the link from Øygarden to the ship that takes it.
function ShorePower({ power }: { power: Data['shorePower'] }) {
  const none = useFixed(0);
  const kw = none(power.kw);
  const rows = useMemo(() => {
    const from = Date.parse(power.from);
    return power.last24h.map((kw, i) => ({ t: from + i * MINUTE_MS, kw }));
  }, [power]);
  return (
    <div className="space-y-2 pt-5">
      <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-secondary-500">
        Shore power from Øygarden
        <span className="ml-1.5 font-normal normal-case tracking-normal">last 24 h</span>
      </p>
      <div className="flex items-center gap-5">
        <div className="h-12 min-w-0 flex-1" aria-label="Shore power, last 24 h" role="img">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
              <YAxis hide domain={[0, 'auto']} />
              <XAxis hide dataKey="t" type="number" domain={['dataMin', 'dataMax']} />
              <Tooltip
                contentStyle={tooltipStyle}
                itemStyle={tooltipItemStyle}
                labelStyle={tooltipLabelStyle}
                labelFormatter={t => formatLocalDate(t as number, 'd MMM HH:mm')}
                formatter={v => (typeof v === 'number' ? `${none(v)} kW` : v)}
                isAnimationActive={false}
              />
              <Area name="Shore power" dataKey="kw" stroke={TEAL} strokeWidth={1.5} fill={TEAL} fillOpacity={0.12} dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <End icon={<Ship aria-hidden className="size-4" />} title="The ship at port at Øygarden">
            {power.vessel ?? <span className="font-normal text-secondary-600">No ship</span>}
          </End>
          <span className="h-px min-w-2 flex-1 bg-secondary-300" />
          {/* Teal, the colour of the graph. */}
          <p className="whitespace-nowrap text-xl font-semibold tabular-nums tracking-tight" style={{ color: TEAL }}>
            {kw ?? <span className="text-secondary-600">{DASH}</span>}
            {kw != null && <span className="ml-1 text-sm font-medium tracking-normal text-secondary-500">kW</span>}
          </p>
          <span className="h-px min-w-2 flex-1 bg-secondary-300" />
          <End icon={<Factory aria-hidden className="size-4" />}>Øygarden</End>
        </div>
      </div>
    </div>
  );
}

// One end of the shore power link: its icon above its name. A long name wraps to a second line rather than being cut.
function End({ icon, title, children }: { icon: ReactNode; title?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 max-w-24 flex-col items-center gap-0.5 text-xs font-medium text-secondary-900" title={title}>
      <span className="text-secondary-500">{icon}</span>
      <span className="line-clamp-2 max-w-full text-center leading-tight">{children}</span>
    </div>
  );
}

// The title of a domain: stronger than a section heading, with the rule that starts the domain.
function DomainTitle({ children }: { children: ReactNode }) {
  return <h2 className="border-b border-secondary-300 pb-2 text-lg font-semibold text-secondary-900">{children}</h2>;
}

// The page header of both apps: what leads it (the brand, standalone), when the snapshot was made, and the
// actions on the right. A failed refresh keeps the last snapshot on screen and says so.
export function OverviewHeader({ data, isError, isLoading, leading, actions }: {
  data: Data | undefined;
  isError: boolean;
  isLoading: boolean;
  leading?: ReactNode;
  actions?: ReactNode;
}) {
  const at = data ? formatLocalDate(data.asOf, 'HH:mm:ss') : null;
  // Standalone (with the brand) the header has no rule; in the portal it is the page header with its rule.
  return (
    <div className={twMerge('flex flex-wrap items-center justify-between gap-x-4 gap-y-3', leading == null && 'border-b border-secondary-200 pb-4')}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        {leading}
        {isError
          ? (
              <p className="flex items-center gap-1 text-xs text-red-700">
                <TriangleAlert aria-hidden className="size-[0.8125rem]" />
                {at == null ? 'Live data unavailable' : `Update failed · showing ${at}`}
              </p>
            )
          : <p className="text-xs text-secondary-600">{at == null ? (isLoading ? 'Loading' : '') : `Updated ${at}`}</p>}
      </div>
      {actions}
    </div>
  );
}

function Section({ title, note, aside, className, children }: { title: ReactNode; note?: string; aside?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <section className={twMerge('flex min-w-0 flex-col gap-3 pt-6', className)}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-secondary-600">
          {title}
          {note != null && <span className="ml-1.5 font-normal normal-case tracking-normal text-secondary-500">{note}</span>}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

// A fixed number of decimals, a true minus, and null for a missing value.
function useFixed(decimals: number) {
  const fmt = useNumberFormatter({ minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return (value: number | null | undefined) => (value == null ? null : fmt.format(value).replace('-', '−'));
}

// A number with its muted unit, or the dash glyph alone.
function Value({ text, unit, className }: { text: string | null; unit: string; className?: string }) {
  if (text == null)
    return <span className={twMerge('tabular-nums text-secondary-600', className)}>{DASH}</span>;
  return (
    <span className={twMerge('whitespace-nowrap tabular-nums text-secondary-900', className)}>
      {text}
      <span className="ml-1 text-xs font-normal text-secondary-500">{unit}</span>
    </span>
  );
}

// KPI tiles side by side. A tile that gets narrow takes a smaller value and wraps to the next line before it
// would overflow, so every value stays on one line with its unit.
function Kpis({ items, className }: { items: [string, string | null, string, ReactNode?][]; className?: string }) {
  return (
    <div className={twMerge('grid grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] gap-x-6 gap-y-4 pt-5', className)}>
      {items.map(([label, text, unit, suffix]) => (
        <div key={label} className="@container min-w-0 space-y-1">
          <p className="truncate text-[0.6875rem] font-semibold uppercase tracking-wider text-secondary-500" title={label}>{label}</p>
          {text == null
            ? <p className="text-2xl font-semibold tabular-nums text-secondary-600">{DASH}</p>
            : (
                <p className="whitespace-nowrap text-xl font-semibold tabular-nums tracking-tight text-secondary-900 @[8rem]:text-2xl">
                  {text}
                  <span className="ml-1 text-sm font-medium tracking-normal text-secondary-500">{unit}</span>
                  {suffix != null && <span className="ml-2 text-sm font-normal tracking-normal text-secondary-500">{suffix}</span>}
                </p>
              )}
        </div>
      ))}
    </div>
  );
}

// Label and value rows split by thin rules.
// Label and value rows split by thin rules. An optional suffix gets its own right-aligned column, so the values and
// the suffixes each line up.
function Rows({ rows }: { rows: [string, ReactNode, ReactNode?][] }) {
  const suffixed = rows.some(row => row[2] != null);
  return (
    <table className="w-full text-sm">
      <tbody className="divide-y divide-secondary-100">
        {rows.map(([label, value, suffix]) => (
          <tr key={label}>
            <td className="w-0 py-2 pr-4 whitespace-nowrap text-secondary-600">{label}</td>
            <td className="py-2 text-right tabular-nums whitespace-nowrap">{value}</td>
            {suffixed && <td className="w-0 py-2 pl-1.5 text-right text-xs tabular-nums whitespace-nowrap text-secondary-500">{suffix}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// One flow's totals under its own small label, so liquid and vapour return stay apart under one heading.
function TotalsPart({ label, flow, minus, asOf, className }: { label: ReactNode; flow: OverviewFlow; minus?: OverviewFlow; asOf: string; className?: string }) {
  return (
    <div className={twMerge('min-w-0', className)}>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-secondary-500">{label}</p>
      <Totals flow={flow} minus={minus} asOf={asOf} />
    </div>
  );
}

// A counter's t at the snapshot time `asOf` plus the time since, as the portal's injection counter shows it: about
// 5 minutes behind, counting up smoothly to the kg (3 decimals), never down. `since` starts a new count (a new year).
const KG = 0.001;

function useCounter(points: OverviewCounterPoint[] | undefined, asOf: string, since?: string) {
  const series = useMemo(() => (points ?? []).map(p => ({ tMs: Date.parse(p.at), v: p.t })), [points]);
  return useTicking(series, asOf, { step: KG, epoch: since }).value;
}

type Period = 'todayFromT' | 'monthFromT' | 'yearFromT';

// The MRV figures since the start of the UTC day, month and year, each followed by the vapour return as a muted −X
// suffix when minus is given (Q3).
function Totals({ flow, minus, asOf }: { flow: OverviewFlow; minus?: OverviewFlow | undefined; asOf: string }) {
  const kg = useFixed(3);
  const liquid = useCounter(flow.counter?.points, asOf, flow.counter?.since);
  const vapour = useCounter(minus?.counter?.points, asOf, minus?.counter?.since);
  // Never below 0: in the first minutes of a period the display is still in the previous one.
  const since = (counter: OverviewFlow['counter'] | undefined, t: number | null, period: Period) =>
    counter == null || t == null ? null : Math.max(0, t - counter[period]);
  const row = (label: string, period: Period): [string, ReactNode, ReactNode?] => {
    const returned = kg(since(minus?.counter, vapour, period));
    return [
      label,
      <Value key="t" text={kg(since(flow.counter, liquid, period))} unit="t" />,
      returned != null ? <span style={{ color: SAND }} title="Vapour return">{`−${returned}`}</span> : undefined,
    ];
  };
  return <Rows rows={[row('Today', 'todayFromT'), row('This month', 'monthFromT'), row('This year', 'yearFromT')]} />;
}

// All CO₂ stored: the Q4 counter since injection started, styled as the portal home page's counter (tonnes in the
// primary colour, then kg).
function Stored({ points, asOf }: { points: OverviewCounterPoint[]; asOf: string }) {
  const none = useFixed(0);
  const t = useCounter(points, asOf);
  // t is whole kg (KG steps), so rounding the fraction gives its kg.
  const kg = t == null ? null : Math.round((t - Math.floor(t)) * 1000);
  return (
    // Centred in the space the wells leave.
    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-2 text-center">
      {t == null || kg == null
        ? <p className="text-5xl font-bold text-secondary-600">{DASH}</p>
        : (
            <p className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-1">
              <span className="flex items-baseline gap-x-2">
                <span className="text-5xl leading-none font-bold tracking-tight tabular-nums text-primary-700">{none(Math.floor(t))}</span>
                <span className="text-xl text-secondary-800">tonnes</span>
              </span>
              <span className="flex items-baseline gap-x-1.5">
                <span className="inline-block w-[3ch] text-right text-3xl leading-none font-semibold tracking-tight tabular-nums text-secondary-800">{kg}</span>
                <span className="text-base text-secondary-800">
                  kg
                  <span className="text-secondary-500">*</span>
                </span>
              </span>
            </p>
          )}
      <p className="text-xs text-secondary-500">* Estimate number, not to be used for reporting</p>
    </div>
  );
}

// Experiment: the well's readings on a schematic of the well.
function Well({ well }: { well: Data['wells'][number] }) {
  const one = useFixed(1);
  const reading = (value: number | null, unit: string) => (value == null ? null : `${one(value)} ${unit}`);
  return (
    <WellSchematic
      chokePct={reading(well.chokeOpeningPct, '%')}
      rate={reading(well.injectionRateMtPerHour, 't/h')}
      wellheadTemp={reading(well.wellheadTemperatureC, '°C')}
      wellheadPressure={reading(well.wellheadPressureBar, 'bar')}
      downholeTemp={reading(well.downholeTemperatureC, '°C')}
      downholePressure={reading(well.downholePressureBar, 'bar')}
      injecting={running(well.injectionRateMtPerHour)}
      label={`${well.name} well schematic`}
    />
  );
}

// One upright bar per tank: the level above it, the tank letter below.
function TankBars({ tanks }: { tanks: Data['tankFarm']['tanks'] }) {
  const none = useFixed(0);
  return (
    <div className="flex h-28 gap-1.5">
      {tanks.map((tank) => {
        const pct = Math.min(100, Math.max(0, tank.levelPct ?? 0));
        return (
          <div key={tank.name} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <div className="flex w-full flex-1 flex-col items-center justify-end gap-1">
              <span className="text-[0.625rem] tabular-nums whitespace-nowrap text-secondary-800">{tank.levelPct == null ? DASH : `${none(tank.levelPct)}%`}</span>
              <div className="relative w-full max-w-5 overflow-hidden rounded-sm bg-secondary-100" style={{ height: '75%' }}>
                <div className="absolute inset-x-0 bottom-0 bg-primary-600" style={{ height: `${pct}%` }} />
              </div>
            </div>
            <span className="text-xs text-secondary-600">{tank.name}</span>
          </div>
        );
      })}
    </div>
  );
}

function ShipMap({ data, theme }: { data: Data; theme: ThemeMode }) {
  const { current, history, nameById } = useMemo(() => ({
    current: data.vessels.map((v): MapPosition => ({
      transporterId: v.name,
      positionTimestamp: v.positionAt,
      latitude: v.latitude,
      longitude: v.longitude,
      speedOverGround: v.speedKnots,
      headingDegrees: v.headingDegrees,
    })),
    history: data.vessels.flatMap(v => v.trail.map((p): MapPosition => ({ transporterId: v.name, positionTimestamp: p.positionAt, latitude: p.latitude, longitude: p.longitude }))),
    nameById: new Map(data.vessels.map(v => [v.name, v.name])),
  }), [data.vessels]);

  return (
    <VesselMap
      theme={theme}
      current={current}
      history={history}
      vesselNameById={nameById}
      formatTime={time => formatLocalDate(time, 'yyyy-MM-dd HH:mm')}
      emptyText="No vessel positions."
      className="h-[26rem] border-secondary-200"
    />
  );
}

function Vessels({ vessels }: { vessels: Data['vessels'] }) {
  const one = useFixed(1);
  const none = useFixed(0);
  return (
    <ul className="divide-y divide-secondary-100">
      {vessels.map(v => (
        <li key={v.name} className="space-y-1 py-3 text-sm">
          <div className="flex items-baseline justify-between gap-4">
            <h3 className="font-semibold text-secondary-900">{v.name}</h3>
            <Value text={one(v.speedKnots)} unit="kn" />
          </div>
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <p className="min-w-0 text-secondary-800">
              {v.status == null || v.port == null
                ? <span className="text-secondary-600">No voyage near now</span>
                : `${v.status === 'AtPort' ? 'At' : 'En route to'} ${v.port}`}
              {v.portTime != null && (
                <span className="ml-2 text-xs whitespace-nowrap text-secondary-500">
                  {`${v.status === 'AtPort' ? 'ETD' : 'ETA'} ${formatLocalDate(v.portTime, 'd MMM HH:mm')}`}
                </span>
              )}
            </p>
            <p className="whitespace-nowrap text-secondary-600">
              <Value text={none(v.onBoardMt)} unit="t" />
              {v.onBoardMt != null && ' on board'}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

// One row per minute of the series, at its time in ms.
function useSeriesRows(series: Data['series']) {
  return useMemo(() => {
    const from = Date.parse(series.from);
    return series.q4.map((_, i) => ({
      t: from + i * MINUTE_MS,
      q3Liquid: series.q3Liquid[i] ?? null,
      q3Vapour: series.q3Vapour[i] ?? null,
      q4: series.q4[i] ?? null,
      q5A7: series.q5A7[i] ?? null,
      q5C1: series.q5C1[i] ?? null,
    }));
  }, [series]);
}

// The value axes of both graphs. They sit side by side, so both reach the same share below 0 and 0 lines up.
function scales(rows: ReturnType<typeof useSeriesRows>) {
  const liquid = rows.map(r => r.q3Liquid);
  const vapour = rows.map(r => r.q3Vapour);
  const injection = rows.flatMap(r => [r.q4, r.q5A7, r.q5C1]);
  const [q3Left, q3Right, q4] = [liquid, vapour, injection].map(v => roundTicks(maxOf(v), 4)) as [number[], number[], number[]];
  const below = Math.max(belowZero(liquid, q3Left), belowZero(vapour, q3Right), belowZero(injection, q4));
  return { q3Left, q3Right, q4, below };
}

// Ticks on every third local hour of the 12 h window.
function hourTicks(rows: { t: number }[]) {
  const [first, last] = [rows[0]?.t ?? 0, rows.at(-1)?.t ?? 0];
  const start = new Date(first);
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + (start.getTime() < first ? 1 : 0));
  start.setHours(start.getHours() + ((3 - (start.getHours() % 3)) % 3));
  const ticks = [];
  for (let t = start.getTime(); t <= last; t += 3 * 60 * MINUTE_MS)
    ticks.push(t);
  return ticks;
}

// 0 to a round top in `count` equal steps.
function roundTicks(max: number, count: number) {
  const step = [1, 2, 2.5, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000].find(s => s * count >= max) ?? Math.ceil(max / count);
  return Array.from({ length: count + 1 }, (_, i) => i * step);
}

function maxOf(values: (number | null)[]) {
  return Math.max(1, ...values.map(v => v ?? 0));
}

// How far the lowest value (meter noise) dips below 0, as a share of the axis top.
function belowZero(values: (number | null)[], ticks: number[]) {
  return Math.max(0, -Math.min(...values.map(v => v ?? 0))) / (ticks.at(-1) ?? 1);
}

// The shared parts of both charts: grid, time axis, value axes and the tooltip. Both value axes reach the same
// share `below` 0, so 0 lines up.
function axes(rows: { t: number }[], ticks: number[], below: number, rightTicks?: number[]) {
  const domain = (t: number[]) => [-below * (t.at(-1) ?? 1), t.at(-1) ?? 1];
  return [
    <CartesianGrid key="grid" stroke={gridStroke} vertical={false} />,
    <XAxis key="x" dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']} ticks={hourTicks(rows)} tickFormatter={t => formatLocalDate(t as number, 'HH:mm')} tick={TICK} tickLine={false} axisLine={{ stroke: gridStroke }} />,
    <YAxis key="y" yAxisId="left" domain={domain(ticks)} ticks={ticks} tick={TICK} tickLine={false} axisLine={false} width="auto" />,
    ...(rightTicks ? [<YAxis key="y2" yAxisId="right" orientation="right" domain={domain(rightTicks)} ticks={rightTicks} tick={TICK} tickLine={false} axisLine={false} width="auto" />] : []),
    <Tooltip
      key="tooltip"
      contentStyle={tooltipStyle}
      itemStyle={tooltipItemStyle}
      labelStyle={tooltipLabelStyle}
      labelFormatter={t => formatLocalDate(t as number, 'HH:mm')}
      formatter={v => (typeof v === 'number' ? `${v.toFixed(1)} t/h` : v)}
      isAnimationActive={false}
    />,
  ];
}

function Chart({ legend, children }: { legend: ReactNode; children: ReactElement }) {
  return (
    <>
      <div className="h-44 w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-secondary-600">{legend}</div>
    </>
  );
}

function Q3Chart({ data }: { data: Data }) {
  const rows = useSeriesRows(data.series);
  const { q3Left, q3Right, below } = scales(rows);
  const legend = (
    <>
      <LegendItem swatch={<LegendSwatch color={TEAL} />}>Liquid (left axis)</LegendItem>
      <LegendItem swatch={<LegendSwatch color={SAND} />}>Vapour return (right axis)</LegendItem>
    </>
  );
  return (
    <Chart legend={legend}>
      <ComposedChart data={rows} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
        {axes(rows, q3Left, below, q3Right)}
        <Line yAxisId="left" name="Q3 liquid" dataKey="q3Liquid" stroke={TEAL} strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line yAxisId="right" name="Q3 vapour return" dataKey="q3Vapour" stroke={SAND} strokeWidth={2} dot={false} isAnimationActive={false} />
      </ComposedChart>
    </Chart>
  );
}

function InjectionChart({ data }: { data: Data }) {
  const rows = useSeriesRows(data.series);
  const { q4, below } = scales(rows);
  const legend = (
    <>
      <LegendItem swatch={<LegendSwatch color={TEAL} width={3} />}>Q4 pipeline</LegendItem>
      <LegendItem swatch={<LegendSwatch color={SAND} />}>Q5 well A-7</LegendItem>
      <LegendItem swatch={<LegendSwatch color={CORAL} />}>Q5 well C-1</LegendItem>
    </>
  );
  return (
    <Chart legend={legend}>
      <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        {axes(rows, q4, below)}
        <Area yAxisId="left" name="Q4 pipeline" dataKey="q4" stroke={TEAL} strokeWidth={2} fill={TEAL} fillOpacity={0.12} dot={false} isAnimationActive={false} />
        <Line yAxisId="left" name="Q5 well A-7" dataKey="q5A7" stroke={SAND} strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line yAxisId="left" name="Q5 well C-1" dataKey="q5C1" stroke={CORAL} strokeWidth={2} dot={false} isAnimationActive={false} />
      </ComposedChart>
    </Chart>
  );
}
